import { useState } from 'react';

import { copy } from '../../core/copy';
import { decodeLook, encodeLook, type Izgled } from '../../core/look';
import { Btn, HeroPortrait, Modal } from '../components';
import { sfx } from '../audio';
import { earnedSouvenirs } from '../../core/rules';
import { availablePathsOf, contentOf, onboard, playerOf, saveIzgled, useStore } from '../store';
import { IzgledEditor } from './Izgled';

/**
 * Switching between ADOBE and DAVINCI — and, behind one button, everything about your character.
 *
 * Onboarding promises "možeš da promeniš kasnije, ništa se ne briše", and this is what makes that
 * true for both of them. The path costs almost nothing to support: progress is keyed on node ids
 * and the two paths share every id, so a student who switches keeps everything they have already
 * done (GDD §5.1).
 *
 * The wardrobe lives here rather than on a screen of its own for a progressive-disclosure reason
 * (GDD §3). LIK is the screen that appears after the first cleared island and it is where the
 * character *belongs*; until it exists, a permanent navigation item for appearance would be a
 * fourth thing on the bar on day one. Settings is already where the look picker was, and a member
 * looking to change their face looks where they chose it.
 */
export function PathSwitch({ onClose }: { onClose: () => void }) {
  const server = useStore((s) => s.server);
  const busy = useStore((s) => s.busy);
  const paths = useStore(availablePathsOf);
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const [choice, setChoice] = useState(server?.member.path ?? paths[0]?.id ?? 'adobe');
  const [izgled, setIzgled] = useState<Izgled>(decodeLook(server?.member.look ?? 0));
  const [editing, setEditing] = useState(false);

  if (!server) return null;
  const look = encodeLook(izgled);
  const changed = choice !== server.member.path || look !== server.member.look;
  // Worn on the preview: choosing a hairstyle without the cap you actually wear on it is choosing
  // against a character that does not exist.
  const souvenirs = earnedSouvenirs(content, player);

  return (
    <Modal title={copy.settings.title} onClose={onClose}>
      {/*
        The path disappears while the wardrobe is open, and that is not tidying: nine rows of
        options underneath a two-option question is a screen where the small decision is on top of
        the big one, and the wardrobe is reached by scrolling past the one control in this app that
        changes which curriculum you are on.
      */}
      {editing ? null : (
        <>
          <p className="lbl">{copy.settings.pathLabel}</p>
          <div className="pathgrid" style={{ marginBottom: 18 }}>
            {paths
              .filter((p) => p.active)
              .map((option) => (
                <button
                  key={option.id}
                  className={`pathcard ${choice === option.id ? 'on' : ''}`.trim()}
                  aria-pressed={choice === option.id}
                  aria-label={`${option.name}. ${option.description}`}
                  onClick={() => {
                    sfx.move();
                    setChoice(option.id);
                  }}
                >
                  <div className="nm">{option.name}</div>
                  <div className="ds">{option.description}</div>
                </button>
              ))}
          </div>

          <p className="tiny muted" style={{ marginBottom: 18 }}>
            {copy.settings.pathHint}
          </p>
        </>
      )}

      <p className="lbl">{copy.settings.lookLabel}</p>
      {editing ? (
        <div className="izwrap">
          <IzgledEditor value={izgled} souvenirs={souvenirs} onChange={setIzgled} />
        </div>
      ) : (
        <div className="lookcurrent">
          <HeroPortrait look={izgled} scale={3} souvenirs={souvenirs} />
          <Btn
            variant="ghost"
            onClick={() => {
              sfx.move();
              setEditing(true);
            }}
          >
            {copy.settings.lookEdit}
          </Btn>
        </div>
      )}

      <div className="btn-row" style={{ marginTop: 18 }}>
        <Btn
          variant="primary"
          disabled={!changed || busy}
          onClick={() => {
            // Two writes because they are two different things, and only one of them has to rebuild
            // the world: a path change re-resolves the whole curriculum and comes back through
            // `onboard`, while an appearance is applied locally and told to the room this member is
            // standing in. Sending an unchanged path through `onboard` would rebuild the map for a
            // haircut.
            if (choice !== server.member.path) {
              void onboard(choice, look).then(onClose);
              return;
            }
            void saveIzgled(look).then(onClose);
          }}
        >
          {copy.settings.save}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>
          {copy.settings.cancel}
        </Btn>
      </div>
    </Modal>
  );
}
